import React, { useState } from "react";
import "./Experience.css";
import {
  Briefcase,
  Calendar,
  MapPin,
  ChevronDown,
  Monitor,
  Globe,
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  Layers,
  Code2,
  Terminal,
} from "lucide-react";

export const DEFAULT_EXPERIENCES = [
  {
    id: "dreamit",
    company: "DreamIT",
    role: "Software Developer · QA/QC Analyst",
    period: "2025 – Present",
    isCurrent: true,
    type: "Professional Experience",
    location: "Sulaymaniyah, Iraq",
    description:
      "My responsibilities evolved from application development to software testing, quality control, and ERP system analysis.",
    skills: [
      "Software Development",
      "Software Testing",
      "Quality Assurance",
      "Quality Control",
      "ERP Systems",
      "System Analysis",
      "Technical Documentation",
    ],
    sections: [
      {
        title: "Windows Application Development",
        icon: Monitor,
        points: [
          "Worked on developing a Windows desktop application.",
          "Contributed to application functionality and feature implementation.",
          "Gained practical experience in desktop application development and debugging.",
        ],
      },
      {
        title: "Website Development",
        icon: Globe,
        points: [
          "Developed a website from the ground up.",
          "Worked through the website development process, from initial structure to implementation.",
          "Implemented and verified website functionality.",
        ],
      },
      {
        title: "Software Testing, QA/QC & ERP Analysis",
        icon: ShieldCheck,
        points: [
          "Designed and executed comprehensive quality checklists for ERP modules.",
          "Tested required fields, input validation, quantity rules, discount calculations, and business workflows.",
          "Identified and documented software defects with expected and actual results.",
          "Recorded test execution results, including Pass/Fail status and observations.",
          "Prepared structured reports to communicate testing outcomes and quality findings.",
          "Created ERP execution reports covering test coverage, results, and critical findings.",
          "Worked with ERP module classifications, canonical tags, and Where Used, Affects On, and Affected By relationships.",
          "Documented cross-module references and potential system dependencies to support traceability and impact analysis.",
        ],
      },
    ],
  },
  {
    id: "sharp-soft",
    company: "Sharp Soft",
    role: ".NET Programmer",
    period: "September 2024 – October 2025",
    isCurrent: false,
    type: "Professional Experience",
    location: "Sulaymaniyah, Iraq",
    description:
      "Contributed to the development of an ERP system for city management and private-city operations.",
    skills: [
      "C#",
      "ASP.NET MVC",
      ".NET Framework 4.8",
      "SQL Server",
      "T-SQL",
      "Clean Architecture",
      "Kendo UI",
      "jQuery",
      "REST APIs",
      "Swagger",
      "Ninject",
      "IIS",
    ],
    sections: [
      {
        title: "Key Responsibilities & Achievements",
        icon: Code2,
        points: [
          "Developed and maintained features within an ASP.NET MVC application.",
          "Worked with Clean Architecture and SQL Server.",
          "Created and worked with SQL queries, stored procedures, and T-SQL.",
          "Implemented and maintained user interface functionality using Kendo UI and jQuery.",
          "Contributed to apartment management functionality and apartment-related statements.",
          "Worked with API controllers and Swagger for API development and testing.",
          "Used Ninject for dependency injection.",
          "Worked with application deployment and hosting through IIS.",
          "Contributed to application permissions and module-level access control.",
        ],
      },
    ],
  },
  {
    id: "kubak",
    company: "Kubak Tech",
    role: "Backend Developer Intern",
    period: "July 2024 – August 2024",
    isCurrent: false,
    type: "Internship",
    location: "Sulaymaniyah, Iraq",
    description:
      "Gained practical backend development experience by building APIs and working with database integration and authentication.",
    skills: [
      "C#",
      "ASP.NET Core",
      "REST APIs",
      "Entity Framework Core",
      "ASP.NET Core Identity",
      "JWT",
      "SQL",
    ],
    sections: [
      {
        title: "Key Responsibilities & Learning Outcomes",
        icon: Terminal,
        points: [
          "Developed RESTful APIs using ASP.NET Core.",
          "Worked with Entity Framework Core for database access and data management.",
          "Implemented authentication and authorization concepts using ASP.NET Core Identity and JWT.",
          "Practiced backend architecture, API development, and database integration.",
          "Strengthened backend development skills through practical project work.",
        ],
      },
    ],
  },
];

function ExperienceItem({ experience, expanded, onToggle }) {
  return (
    <article
      className={`experience-item ${experience.isCurrent ? "is-current" : ""}`}
    >
      <div className="experience-marker" aria-hidden="true">
        <span className="experience-dot" />
      </div>

      <div className="experience-card">
        <div className="experience-topline">
          <span className="experience-type-badge">
            <Briefcase size={12} />
            {experience.type}
          </span>
          <span className="experience-period-badge">
            <Calendar size={13} />
            {experience.period}
          </span>
        </div>

        <div className="experience-heading">
          <div>
            <h3>{experience.company}</h3>
            <div className="experience-role-row">
              <p className="experience-role">{experience.role}</p>
            </div>
          </div>
        </div>

        <p className="experience-description">{experience.description}</p>

        {experience.location && (
          <p className="experience-location">
            <MapPin size={13} className="experience-location-icon" />
            {experience.location}
          </p>
        )}

        <div className="experience-skills">
          {experience.skills.map((skill) => (
            <span className="experience-skill" key={skill}>
              {skill}
            </span>
          ))}
        </div>

        <button
          className="experience-toggle"
          type="button"
          aria-expanded={expanded}
          aria-controls={`experience-details-${experience.id}`}
          onClick={onToggle}
        >
          <span className="experience-toggle-content">
            <Layers size={15} />
            {expanded ? "Hide details" : "Explore experience"}
          </span>
          <span
            className={`experience-chevron ${expanded ? "is-open" : ""}`}
            aria-hidden="true"
          >
            <ChevronDown size={16} />
          </span>
        </button>

        {expanded && (
          <div
            className="experience-details"
            id={`experience-details-${experience.id}`}
            role="region"
            aria-label={`${experience.company} details`}
          >
            {experience.sections.map((section) => {
              const SectionIcon = section.icon || CheckCircle2;
              return (
                <div className="experience-subsection" key={section.title}>
                  <div className="experience-subsection-header">
                    <span className="experience-subsection-icon">
                      <SectionIcon size={16} />
                    </span>
                    <h4>{section.title}</h4>
                  </div>
                  <ul>
                    {section.points.map((point, i) => (
                      <li key={i}>
                        <span
                          className="experience-bullet-icon"
                          aria-hidden="true"
                        />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </article>
  );
}

export default function Experience({ data = DEFAULT_EXPERIENCES }) {
  // Map of expanded item IDs; DreamIT is expanded by default (index 0)
  const [expandedMap, setExpandedMap] = useState(() => {
    const initial = {};
    data.forEach((exp, idx) => {
      initial[exp.id] = idx === 0;
    });
    return initial;
  });

  const toggleItem = (id) => {
    setExpandedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const allExpanded = data.every((exp) => expandedMap[exp.id]);

  const toggleAll = () => {
    const nextState = !allExpanded;
    const updated = {};
    data.forEach((exp) => {
      updated[exp.id] = nextState;
    });
    setExpandedMap(updated);
  };

  return (
    <section className="experience-section" id="experience">
      <div className="experience-container">
        <header className="experience-header">
          <span className="experience-eyebrow">
            <Sparkles size={13} />
            MY JOURNEY
          </span>

          <h2>
            Professional <span>Experience</span>
          </h2>

          <p>
            My journey through software development, backend engineering,
            quality assurance, and ERP systems.
          </p>
        </header>

        <div className="experience-controls">
          <button
            type="button"
            className="experience-control-btn"
            onClick={toggleAll}
            aria-label={allExpanded ? "Collapse all positions" : "Expand all positions"}
          >
            <ChevronDown
              size={14}
              className={`transition-transform duration-300 ${
                allExpanded ? "rotate-180" : "rotate-0"
              }`}
            />
            {allExpanded ? "Collapse All" : "Expand All"}
          </button>
        </div>

        <div className="experience-timeline">
          {data.map((experience) => (
            <ExperienceItem
              key={experience.id}
              experience={experience}
              expanded={!!expandedMap[experience.id]}
              onToggle={() => toggleItem(experience.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
